import { useState, useRef, useEffect, useCallback } from 'react';
import {
  Tile,
  TextInput,
  Button,
  TextArea,
  Select,
  SelectItem,
  InlineNotification,
  Tag,
  Modal,
  Checkbox,
  ProgressIndicator,
  ProgressStep,
  ContentSwitcher,
  Switch,
  TextInput as CarbonTextInput,
} from '@carbon/react';
import {
  Send,
  Download,
  Edit,
  CheckmarkFilled,
  Pen,
  Copy,
  ArrowLeft,
  Link as LinkIcon,
  DocumentPdf,
} from '@carbon/icons-react';
import CarbonDateField from '@/components/CarbonDateField';
import { CONTRACT_TEMPLATES } from '@/lib/contractTemplates';
import { createDocument, getPublicUrl } from '@/lib/documentsApi';
import {
  createContractPdfBlobUrl,
  downloadContractPdf,
  revokePdfBlobUrl,
  buildSignedContractText,
} from '@/lib/contractPdf';
import { useAuth } from '@/contexts/AuthContext';

const ContractGenerator = ({ onSent, embedded = false }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [previewMode, setPreviewMode] = useState(0);
  const [selectedTemplate, setSelectedTemplate] = useState('service_agreement');
  const [contractData, setContractData] = useState({
    date: new Date().toISOString().split('T')[0],
    companyName: 'SenseStack Pty Ltd',
    companyAddress: 'Australia',
    companyEmail: 'inquiry@sensestack.com.au',
    companyPhone: '+61 (03) 9010 6148',
    companyAbn: '14187234379',
    companySigner: '',
    clientName: '',
    clientAddress: '',
    clientEmail: '',
    clientSigner: '',
    services: '',
    startDate: '',
    endDate: '',
    jurisdiction: 'Australia',
    ndaTerm: 'three (3) years',
    projectOverview: '',
    scopeOfWork: '',
    projectDuration: '',
    projectValue: '',
    paymentTerms: '50% upon commencement, 50% upon completion',
    validUntil: '',
  });

  const [generatedContract, setGeneratedContract] = useState('');
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState('');
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [signatureData, setSignatureData] = useState(null);
  const [isSigned, setIsSigned] = useState(false);
  const [notification, setNotification] = useState(null);
  const [showSendModal, setShowSendModal] = useState(false);
  const [sendEmail, setSendEmail] = useState('');
  const [includeSignature, setIncludeSignature] = useState(true);
  const [shareLink, setShareLink] = useState('');
  const [documentToken, setDocumentToken] = useState('');
  const { user } = useAuth();

  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);

  const templateName = CONTRACT_TEMPLATES[selectedTemplate].name;

  const refreshPdfPreview = useCallback(
    (content, companySig = signatureData) => {
      setPdfPreviewUrl((prev) => {
        revokePdfBlobUrl(prev);
        return createContractPdfBlobUrl({
          title: templateName,
          content,
          companySignature: includeSignature ? companySig : null,
          companySigner: contractData.companySigner || contractData.companyName,
        });
      });
    },
    [templateName, signatureData, includeSignature, contractData.companySigner, contractData.companyName]
  );

  useEffect(() => () => revokePdfBlobUrl(pdfPreviewUrl), [pdfPreviewUrl]);

  const generateContract = () => {
    const template = CONTRACT_TEMPLATES[selectedTemplate];
    const contract = template.template(contractData);
    setGeneratedContract(contract);
    refreshPdfPreview(contract);
    setActiveStep(1);
    setNotification({
      kind: 'success',
      title: 'Contract generated',
      subtitle: 'Review the text and PDF preview, then continue to sign and send.',
    });
    setTimeout(() => setNotification(null), 4000);
  };

  const goBack = () => {
    setActiveStep((step) => Math.max(0, step - 1));
  };

  const startSignature = () => {
    setShowSignatureModal(true);
    setTimeout(() => {
      const canvas = canvasRef.current;
      if (canvas) {
        const ctx = canvas.getContext('2d');
        ctx.strokeStyle = '#000';
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';
      }
    }, 100);
  };

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    ctx.beginPath();
    ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    ctx.lineTo(e.clientX - rect.left, e.clientY - rect.top);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
    if (canvasRef.current) {
      setSignatureData(canvasRef.current.toDataURL());
    }
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
      setSignatureData(null);
    }
  };

  const saveSignature = () => {
    const sig = canvasRef.current?.toDataURL() || signatureData;
    setSignatureData(sig);
    setIsSigned(true);
    setShowSignatureModal(false);
    refreshPdfPreview(generatedContract, sig);
    setNotification({ kind: 'success', title: 'Signature added', subtitle: 'PDF preview updated with your signature.' });
    setTimeout(() => setNotification(null), 3000);
  };

  const downloadText = () => {
    const content = buildSignedContractText(generatedContract, {
      companySigner: contractData.companySigner,
    });
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${templateName.replace(/\s+/g, '_')}_${contractData.clientName || 'Client'}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const downloadPdf = () => {
    downloadContractPdf(
      `${templateName.replace(/\s+/g, '_')}_${contractData.clientName || 'Client'}.pdf`,
      {
        title: templateName,
        content: generatedContract,
        companySignature: isSigned && includeSignature ? signatureData : null,
        companySigner: contractData.companySigner || contractData.companyName,
      }
    );
  };

  const sendContract = () => {
    setShowSendModal(true);
    setSendEmail(contractData.clientEmail);
  };

  const confirmSend = async () => {
    if (!generatedContract) {
      setNotification({ kind: 'error', title: 'Generate first', subtitle: 'Create the contract before sending.' });
      return;
    }

    try {
      const doc = await createDocument({
        type: 'contract',
        ownerId: user?.id,
        clientEmail: sendEmail,
        clientName: contractData.clientName,
        status: 'sent',
        data: {
          template: selectedTemplate,
          templateName,
          contractData,
          content: generatedContract,
          companySignature: includeSignature && signatureData ? signatureData : null,
        },
      });

      const link = getPublicUrl('contract', doc.publicToken);
      setShareLink(link);
      setDocumentToken(doc.publicToken);
      navigator.clipboard.writeText(link);
      setShowSendModal(false);
      setNotification({
        kind: 'success',
        title: 'Signing link ready',
        subtitle: 'Link copied — client signs on this SenseStack site.',
      });
      onSent?.();
    } catch (err) {
      setNotification({ kind: 'error', title: 'Send failed', subtitle: err.message });
    }
    setTimeout(() => setNotification(null), 5000);
  };

  const copyLink = () => {
    if (!shareLink) return;
    navigator.clipboard.writeText(shareLink);
    setNotification({ kind: 'success', title: 'Link copied', subtitle: shareLink });
    setTimeout(() => setNotification(null), 2500);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(generatedContract);
    setNotification({ kind: 'success', title: 'Copied', subtitle: 'Contract text copied to clipboard' });
    setTimeout(() => setNotification(null), 2000);
  };

  return (
    <div className="contract-generator">
      {!embedded && (
        <div className="section-header">
          <Tag type="blue">LEGAL DOCUMENTS</Tag>
          <h2 className="section-title">Contract & NDA Generator</h2>
          <p className="section-description">Generate professional contracts and send for e-signature</p>
        </div>
      )}

      {notification && (
        <InlineNotification
          kind={notification.kind}
          title={notification.title}
          subtitle={notification.subtitle}
          lowContrast
          onClose={() => setNotification(null)}
          className="contract-notification"
        />
      )}

      <Tile className="contract-steps-tile">
        <h3 className="contract-steps-title">How it works</h3>
        <ProgressIndicator currentIndex={activeStep} spaceEqually className="contract-progress">
          <ProgressStep label="Fill in details" description="Choose a template and enter client info" />
          <ProgressStep label="Generate & review" description="Text and PDF preview" />
          <ProgressStep label="Sign & send" description="Signature and client signing link" />
        </ProgressIndicator>
      </Tile>

      {activeStep === 0 && (
        <section className="contract-step-section" aria-labelledby="contract-step-1">
          <h3 id="contract-step-1" className="contract-step-heading">
            Step 1 — Fill in contract details
          </h3>
          <Tile className="contract-form-tile">
            <div className="contract-form-grid">
              <div className="contract-section">
                <h3>Template</h3>
                <Select
                  id="template"
                  labelText="Select Document Type"
                  value={selectedTemplate}
                  onChange={(e) => setSelectedTemplate(e.target.value)}
                >
                  {Object.entries(CONTRACT_TEMPLATES).map(([key, template]) => (
                    <SelectItem key={key} value={key} text={template.name} />
                  ))}
                </Select>
                <p className="template-description">
                  {CONTRACT_TEMPLATES[selectedTemplate].description}
                </p>
              </div>

              <div className="contract-section">
                <h3>Your Company</h3>
                <TextInput
                  id="company-name"
                  labelText="Company Name"
                  value={contractData.companyName}
                  onChange={(e) => setContractData({ ...contractData, companyName: e.target.value })}
                />
                <TextArea
                  id="company-address"
                  labelText="Company Address"
                  value={contractData.companyAddress}
                  onChange={(e) => setContractData({ ...contractData, companyAddress: e.target.value })}
                  rows={2}
                />
                <TextInput
                  id="company-email"
                  type="email"
                  labelText="Company Email"
                  value={contractData.companyEmail}
                  onChange={(e) => setContractData({ ...contractData, companyEmail: e.target.value })}
                />
                <TextInput
                  id="company-abn"
                  labelText="ABN/Registration Number"
                  value={contractData.companyAbn}
                  onChange={(e) => setContractData({ ...contractData, companyAbn: e.target.value })}
                />
                <TextInput
                  id="company-signer"
                  labelText="Authorized Signatory Name"
                  value={contractData.companySigner}
                  onChange={(e) => setContractData({ ...contractData, companySigner: e.target.value })}
                  placeholder="e.g., John Smith, CEO"
                />
              </div>

              <div className="contract-section">
                <h3>Client Information</h3>
                <TextInput
                  id="client-name"
                  labelText="Client/Company Name"
                  value={contractData.clientName}
                  onChange={(e) => setContractData({ ...contractData, clientName: e.target.value })}
                  required
                />
                <TextArea
                  id="client-address"
                  labelText="Client Address"
                  value={contractData.clientAddress}
                  onChange={(e) => setContractData({ ...contractData, clientAddress: e.target.value })}
                  rows={2}
                />
                <TextInput
                  id="client-email"
                  type="email"
                  labelText="Client Email"
                  value={contractData.clientEmail}
                  onChange={(e) => setContractData({ ...contractData, clientEmail: e.target.value })}
                />
                <TextInput
                  id="client-signer"
                  labelText="Client Signatory Name"
                  value={contractData.clientSigner}
                  onChange={(e) => setContractData({ ...contractData, clientSigner: e.target.value })}
                  placeholder="e.g., Jane Doe, Director"
                />
              </div>

              <div className="contract-section">
                <h3>Contract Details</h3>
                <CarbonDateField
                  id="contract-date"
                  labelText="Contract Date"
                  value={contractData.date}
                  onChange={(date) => setContractData({ ...contractData, date })}
                />
                <Select
                  id="jurisdiction"
                  labelText="Governing Law"
                  value={contractData.jurisdiction}
                  onChange={(e) => setContractData({ ...contractData, jurisdiction: e.target.value })}
                >
                  <SelectItem value="Australia" text="Australia" />
                  <SelectItem value="New South Wales, Australia" text="New South Wales, Australia" />
                  <SelectItem value="Victoria, Australia" text="Victoria, Australia" />
                  <SelectItem value="United States" text="United States" />
                  <SelectItem value="United Kingdom" text="United Kingdom" />
                  <SelectItem value="Other" text="Other" />
                </Select>

                {selectedTemplate === 'service_agreement' && (
                  <>
                    <TextArea
                      id="services"
                      labelText="Description of Services"
                      value={contractData.services}
                      onChange={(e) => setContractData({ ...contractData, services: e.target.value })}
                      rows={3}
                      placeholder="Describe the services to be provided..."
                    />
                    <CarbonDateField
                      id="start-date"
                      labelText="Service Start Date"
                      value={contractData.startDate}
                      onChange={(startDate) => setContractData({ ...contractData, startDate })}
                    />
                    <CarbonDateField
                      id="end-date"
                      labelText="Service End Date"
                      value={contractData.endDate}
                      minDate={contractData.startDate || undefined}
                      onChange={(endDate) => setContractData({ ...contractData, endDate })}
                    />
                  </>
                )}

                {selectedTemplate === 'nda' && (
                  <TextInput
                    id="nda-term"
                    labelText="NDA Term"
                    value={contractData.ndaTerm}
                    onChange={(e) => setContractData({ ...contractData, ndaTerm: e.target.value })}
                    placeholder="e.g., three (3) years"
                  />
                )}

                {selectedTemplate === 'proposal' && (
                  <>
                    <TextArea
                      id="project-overview"
                      labelText="Project Overview"
                      value={contractData.projectOverview}
                      onChange={(e) => setContractData({ ...contractData, projectOverview: e.target.value })}
                      rows={2}
                    />
                    <TextArea
                      id="scope-of-work"
                      labelText="Scope of Work"
                      value={contractData.scopeOfWork}
                      onChange={(e) => setContractData({ ...contractData, scopeOfWork: e.target.value })}
                      rows={4}
                    />
                    <TextInput
                      id="project-duration"
                      labelText="Project Duration"
                      value={contractData.projectDuration}
                      onChange={(e) => setContractData({ ...contractData, projectDuration: e.target.value })}
                      placeholder="e.g., 3 months"
                    />
                    <TextInput
                      id="project-value"
                      labelText="Total Project Value"
                      value={contractData.projectValue}
                      onChange={(e) => setContractData({ ...contractData, projectValue: e.target.value })}
                      placeholder="e.g., $50,000 AUD"
                    />
                    <TextInput
                      id="payment-terms"
                      labelText="Payment Terms"
                      value={contractData.paymentTerms}
                      onChange={(e) => setContractData({ ...contractData, paymentTerms: e.target.value })}
                    />
                    <CarbonDateField
                      id="valid-until"
                      labelText="Proposal Valid Until"
                      value={contractData.validUntil}
                      minDate={contractData.date || undefined}
                      onChange={(validUntil) => setContractData({ ...contractData, validUntil })}
                    />
                  </>
                )}
              </div>
            </div>

            <div className="contract-actions">
              <Button kind="primary" renderIcon={Edit} onClick={generateContract}>
                Generate Contract
              </Button>
            </div>
          </Tile>
        </section>
      )}

      {activeStep === 1 && generatedContract && (
        <section className="contract-step-section" aria-labelledby="contract-step-2">
          <div className="contract-step-header">
            <h3 id="contract-step-2" className="contract-step-heading">
              Step 2 — Review text and PDF
            </h3>
            <Button kind="secondary" renderIcon={ArrowLeft} onClick={goBack}>
              Back to details
            </Button>
          </div>
          <Tile className="contract-preview-tile">
            <div className="contract-preview-header">
              <h3>{templateName}</h3>
              <div className="contract-preview-actions">
                <Button kind="ghost" size="sm" renderIcon={Copy} onClick={copyToClipboard}>
                  Copy text
                </Button>
                <Button kind="ghost" size="sm" renderIcon={Download} onClick={downloadText}>
                  Download text
                </Button>
                <Button kind="ghost" size="sm" renderIcon={DocumentPdf} onClick={downloadPdf}>
                  Download PDF
                </Button>
              </div>
            </div>

            <ContentSwitcher
              selectedIndex={previewMode}
              onChange={({ index }) => setPreviewMode(index)}
              size="sm"
              className="contract-preview-switcher"
            >
              <Switch name="text" text="Text preview" />
              <Switch name="pdf" text="PDF preview" />
            </ContentSwitcher>

            {previewMode === 0 ? (
              <pre className="contract-content">{generatedContract}</pre>
            ) : (
              <div className="contract-pdf-preview">
                {pdfPreviewUrl ? (
                  <iframe title="Contract PDF preview" src={pdfPreviewUrl} className="contract-pdf-frame" />
                ) : (
                  <p className="contract-pdf-empty">PDF preview loading...</p>
                )}
              </div>
            )}

            <div className="contract-final-actions">
              <Button kind="primary" onClick={() => setActiveStep(2)}>
                Continue to sign & send
              </Button>
            </div>
          </Tile>
        </section>
      )}

      {activeStep === 2 && generatedContract && (
        <section className="contract-step-section" aria-labelledby="contract-step-3">
          <div className="contract-step-header">
            <h3 id="contract-step-3" className="contract-step-heading">
              Step 3 — Sign and share link {isSigned && '✓'}
            </h3>
            <Button kind="secondary" renderIcon={ArrowLeft} onClick={goBack}>
              Back to review
            </Button>
          </div>

          <Tile className="contract-signature-tile">
            {!isSigned ? (
              <div className="signature-empty-state">
                <Pen size={64} />
                <h3>Add your company signature</h3>
                <p>Draw your signature — the PDF preview will update automatically.</p>
                <Button kind="primary" renderIcon={Pen} onClick={startSignature}>
                  Add Signature
                </Button>
              </div>
            ) : (
              <div className="signature-complete">
                <CheckmarkFilled size={64} className="signature-check" />
                <h3>Company signature added</h3>
                {signatureData && (
                  <div className="signature-preview">
                    <img src={signatureData} alt="Your signature" />
                  </div>
                )}
                <div className="signature-actions">
                  <Button kind="secondary" onClick={startSignature}>
                    Redraw Signature
                  </Button>
                  <Button kind="tertiary" renderIcon={DocumentPdf} onClick={downloadPdf}>
                    Download PDF
                  </Button>
                </div>
              </div>
            )}
          </Tile>

          <Tile className="contract-share-tile">
            <h3 className="contract-share-title">Client signing link</h3>
            <p className="contract-share-description">
              Share this link with your client. They will return to this SenseStack page to review,
              draw their signature, submit, and download the updated signed PDF.
            </p>
            <div className="contract-share-actions">
              <Button
                kind="primary"
                renderIcon={Send}
                onClick={sendContract}
                disabled={!contractData.clientEmail}
              >
                Generate signing link
              </Button>
            </div>
            {shareLink && (
              <div className="contract-link-box">
                <CarbonTextInput
                  id="share-link"
                  labelText="Signing URL (opens on this site)"
                  value={shareLink}
                  readOnly
                />
                <Button kind="secondary" renderIcon={Copy} onClick={copyLink}>
                  Copy link
                </Button>
              </div>
            )}
            {documentToken && (
              <p className="send-note">
                Client path: <code>/sign/{documentToken}</code>
              </p>
            )}
            <div className="contract-step-footer">
              <Button kind="secondary" renderIcon={ArrowLeft} onClick={goBack}>
                Back to review
              </Button>
            </div>
          </Tile>
        </section>
      )}

      <Modal
        open={showSignatureModal}
        onRequestClose={() => setShowSignatureModal(false)}
        modalHeading="Add Your Digital Signature"
        primaryButtonText="Save Signature"
        primaryButtonDisabled={!signatureData}
        onRequestSubmit={saveSignature}
        secondaryButtonText="Clear"
        onSecondarySubmit={clearSignature}
      >
        <p>Sign in the box below using your mouse or touchpad:</p>
        <div className="signature-canvas-container">
          <canvas
            ref={canvasRef}
            width={500}
            height={200}
            className="signature-canvas"
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={stopDrawing}
            onMouseLeave={stopDrawing}
          />
        </div>
      </Modal>

      <Modal
        open={showSendModal}
        onRequestClose={() => setShowSendModal(false)}
        modalHeading="Generate Client Signing Link"
        primaryButtonText="Create link"
        onRequestSubmit={confirmSend}
        secondaryButtonText="Cancel"
        onSecondarySubmit={() => setShowSendModal(false)}
      >
        <div className="send-contract-form">
          <TextInput
            id="send-email"
            type="email"
            labelText="Recipient Email"
            value={sendEmail}
            onChange={(e) => setSendEmail(e.target.value)}
          />
          <Checkbox
            id="include-sig"
            labelText="Include my company signature in the document"
            checked={includeSignature}
            onChange={(e) => setIncludeSignature(e.target.checked)}
          />
          <p className="send-note">
            The link will point to <strong>{window.location.origin}/sign/…</strong> on this website.
          </p>
        </div>
      </Modal>
    </div>
  );
};

export default ContractGenerator;
